import React, { useEffect, useRef, useState } from 'react';
import {
  Heart,
  Star,
  Zap,
  Euro,
  Users,
  Crown,
  Sparkles,
  PartyPopper,
  Flame,
  Wallet,
  Radio,
  Gamepad2,
  Youtube,
  Music2,
  TrendingUp,
  Clock,
  History,
  Tv2,
  X,
} from 'lucide-react';
import { useCommunity } from '../hooks/useCommunity';
import useHomepageVisibility from '../hooks/useHomepageVisibility';
import { api } from '../lib/api';

const TICKER_H_FALLBACK_PX = 28;
const SUGG_REFRESH_MS = 60_000; // 1 min
const HISTORY_REFRESH_MS = 5 * 60_000; // 5 min — backed by 5-min server cache
const DISMISSED_KEY = 'jdv_stats_ticker_dismissed_v1';

// Human-readable Dutch relative time for the "last live" pill.
function relativeNL(epochMs) {
  if (!epochMs) return null;
  const diff = Date.now() - epochMs;
  if (diff < 0) return 'nu';
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'zojuist';
  if (m < 60) return `${m} min geleden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} uur geleden`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d geleden`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w geleden`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} mnd geleden`;
  return `${Math.floor(d / 365)}j geleden`;
}

/**
 * Slim horizontal stats marquee — live data from StreamElements via
 * /api/community/overview. Mixes session totals, all-time totals and the
 * single latest event in each category. Designed to sit at the top of the
 * homepage, just above the navbar.
 */
export default function StatsTicker() {
  const { data } = useCommunity();
  const {
    stats_ticker: visible,
    stats_ticker_position: position,
    stats_ticker_items: enabledKeys,
    stats_ticker_duration_s: durationS,
  } = useHomepageVisibility();
  const [sugg, setSugg] = useState(null);
  const [history, setHistory] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const wrapperRef = useRef(null);

  // Poll the suggestions ticker stats independently of community data.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get('/suggestions/ticker-stats');
        if (alive) setSugg(r.data);
      } catch {
        /* non-critical */
      }
    };
    load();
    const id = setInterval(load, SUGG_REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Poll stream history (last live + total hours). 5-minute cache server-side.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get('/stream-history/summary');
        if (alive) setHistory(r.data || null);
      } catch {
        /* non-critical */
      }
    };
    load();
    const id = setInterval(load, HISTORY_REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Publish ticker height as CSS vars so the navbar (top-mode) and other
  // bottom-anchored elements (bottom-mode) can offset themselves. We
  // MEASURE the real rendered height each frame the layout changes so the
  // navbar sits flush against the bar (a hardcoded value would leak the
  // page background through any px difference). Cleared on unmount so
  // non-home pages keep their layout intact.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const active = !!(data && visible && !dismissed);
    const atBottom = position !== 'top';
    const root = document.documentElement;
    if (!active) {
      root.style.setProperty('--stats-ticker-h', '0px');
      root.style.setProperty('--stats-ticker-top-h', '0px');
      root.style.setProperty('--stats-ticker-gap', '0px');
      return () => {
        root.style.setProperty('--stats-ticker-h', '0px');
        root.style.setProperty('--stats-ticker-top-h', '0px');
        root.style.setProperty('--stats-ticker-gap', '0px');
      };
    }

    const el = wrapperRef.current;
    const apply = (h) => {
      root.style.setProperty('--stats-ticker-h', atBottom ? `${h}px` : '0px');
      root.style.setProperty('--stats-ticker-top-h', !atBottom ? `${h}px` : '0px');
      root.style.setProperty('--stats-ticker-gap', '0px');
    };
    apply(TICKER_H_FALLBACK_PX);

    let raf = 0;
    const measure = () => {
      if (!el) return;
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) apply(h);
    };
    raf = requestAnimationFrame(measure);
    const ro = typeof ResizeObserver !== 'undefined' && el
      ? new ResizeObserver(measure)
      : null;
    if (ro && el) ro.observe(el);
    window.addEventListener('resize', measure);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      root.style.setProperty('--stats-ticker-h', '0px');
      root.style.setProperty('--stats-ticker-top-h', '0px');
      root.style.setProperty('--stats-ticker-gap', '0px');
    };
  }, [data, visible, dismissed, position]);

  if (!visible) return null;
  if (dismissed) return null;
  if (!data) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
      /* ignore */
    }
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--stats-ticker-h', '0px');
      document.documentElement.style.setProperty('--stats-ticker-top-h', '0px');
      document.documentElement.style.setProperty('--stats-ticker-gap', '0px');
    }
    setDismissed(true);
  };

  const f  = data.followers || {};
  const s  = data.subs      || {};
  const b  = data.bits      || {};
  const d  = data.dono      || {};

  const fSession = f.stats?.session ?? 0;
  const sSession = s.stats?.session ?? 0;
  const bSession = b.stats?.session ?? 0;
  const dSession = Number(d.stats?.session ?? 0);

  const fTotal   = f.stats?.total ?? 0;
  const sTotal   = s.stats?.total ?? 0;
  const dTotal   = Number(d.stats?.total ?? 0);

  const lastFollower = f.recent?.[0]?.username;
  const lastSub      = s.recent?.[0]?.username;
  const lastTip      = d.recent?.[0];
  const lastCheer    = b.recent?.[0];

  // Each item is { icon, label, value, accent } — pill rendering below.
  const items = [];

  // --- Session totals ---
  if (fSession || true) items.push({ key: 'session_followers', icon: Heart,  label: 'Volgers sessie',  value: `+${fSession}`,        accent: 'text-pink-300' });
  if (sSession || true) items.push({ key: 'session_subs',      icon: Star,   label: 'Subs sessie',     value: `+${sSession}`,        accent: 'text-amber-300' });
  if (bSession || true) items.push({ key: 'session_bits',      icon: Zap,    label: 'Bits sessie',     value: `+${bSession}`,        accent: 'text-violet-300' });
  if (dSession || true) items.push({ key: 'session_tips',      icon: Euro,   label: 'Tips sessie',     value: `€${dSession.toFixed(0)}`, accent: 'text-emerald-300' });

  // --- All-time totals ---
  items.push({ key: 'total_followers', icon: Users,  label: 'Volgers totaal', value: fTotal.toLocaleString('nl-NL'), accent: 'text-fuchsia-300' });
  items.push({ key: 'total_subs',      icon: Crown,  label: 'Subs totaal',    value: sTotal.toLocaleString('nl-NL'), accent: 'text-amber-200' });
  items.push({ key: 'total_dono',      icon: Wallet, label: 'Opgehaald',      value: `€${dTotal.toFixed(0)}`,        accent: 'text-emerald-200' });

  // --- Latest events ---
  if (lastFollower) {
    items.push({ key: 'last_follower', icon: PartyPopper, label: 'Laatste volger', value: `@${lastFollower}`, accent: 'text-pink-200' });
  }
  if (lastSub) {
    items.push({ key: 'last_sub', icon: Sparkles, label: 'Laatste sub', value: `@${lastSub}`, accent: 'text-amber-200' });
  }
  if (lastTip?.username && lastTip?.amount != null) {
    items.push({
      key: 'last_tip',
      icon: Heart,
      label: 'Laatste tip',
      value: `@${lastTip.username} · €${Number(lastTip.amount).toFixed(0)}`,
      accent: 'text-emerald-200',
    });
  }
  if (lastCheer?.username && lastCheer?.amount != null) {
    items.push({
      key: 'last_cheer',
      icon: Flame,
      label: 'Laatste cheer',
      value: `@${lastCheer.username} · ${lastCheer.amount} bits`,
      accent: 'text-violet-200',
    });
  }

  // --- Stream history (Twitch VOD archive) ---
  const last = history?.last_stream;
  const allTime = history?.all_time;
  if (last?.ended_at) {
    const rel = relativeNL(last.ended_at);
    if (rel) {
      items.push({
        key: 'last_live',
        icon: Clock,
        label: 'Laatste live',
        value: rel,
        accent: 'text-sky-300',
      });
    }
  }
  if (last?.game_name) {
    items.push({
      key: 'last_game',
      icon: Tv2,
      label: 'Laatste game',
      value: last.game_name,
      accent: 'text-cyan-200',
    });
  }
  if (typeof allTime?.hours === 'number' && allTime.hours > 0) {
    items.push({
      key: 'all_time_hours',
      icon: History,
      label: 'Live (60 dagen)',
      value: `${allTime.hours.toLocaleString('nl-NL')} uur`,
      accent: 'text-sky-200',
    });
  }

  // --- Suggestion stats ---
  const truncate = (txt, n = 24) =>
    !txt ? '' : (txt.length > n ? `${txt.slice(0, n - 1)}…` : txt);

  if (sugg?.games) {
    const g = sugg.games;
    items.push({ key: 'sugg_games_count', icon: Gamepad2, label: 'Game-suggesties', value: String(g.count ?? 0), accent: 'text-violet-200' });
    if (g.top?.title) {
      items.push({
        key: 'sugg_games_top',
        icon: TrendingUp,
        label: 'Top game',
        value: `${truncate(g.top.title)} · ${g.top.votes} stem${g.top.votes === 1 ? '' : 'men'}`,
        accent: 'text-fuchsia-200',
      });
    }
    if (g.latest?.title) {
      items.push({
        key: 'sugg_games_latest',
        icon: Gamepad2,
        label: 'Nieuwste game-suggestie',
        value: `${truncate(g.latest.title)} — @${g.latest.by}`,
        accent: 'text-violet-100',
      });
    }
  }
  if (sugg?.videos) {
    const v = sugg.videos;
    items.push({ key: 'sugg_videos_count', icon: Youtube, label: 'Video-suggesties', value: String(v.count ?? 0), accent: 'text-rose-300' });
    if (v.top?.title) {
      items.push({
        key: 'sugg_videos_top',
        icon: TrendingUp,
        label: 'Top video',
        value: `${truncate(v.top.title)} · ${v.top.votes} stem${v.top.votes === 1 ? '' : 'men'}`,
        accent: 'text-rose-200',
      });
    }
    if (v.latest?.title) {
      items.push({
        key: 'sugg_videos_latest',
        icon: Youtube,
        label: 'Nieuwste video-suggestie',
        value: `${truncate(v.latest.title)} — @${v.latest.by}`,
        accent: 'text-rose-100',
      });
    }
  }
  if (sugg?.music) {
    const m = sugg.music;
    items.push({ key: 'sugg_music_count', icon: Music2, label: 'Muziek-suggesties', value: String(m.count ?? 0), accent: 'text-emerald-300' });
    if (m.top?.title) {
      items.push({
        key: 'sugg_music_top',
        icon: TrendingUp,
        label: 'Top muziek',
        value: `${truncate(m.top.title)} · ${m.top.votes} stem${m.top.votes === 1 ? '' : 'men'}`,
        accent: 'text-emerald-200',
      });
    }
    if (m.latest?.title) {
      items.push({
        key: 'sugg_music_latest',
        icon: Music2,
        label: 'Nieuwste muziek-suggestie',
        value: `${truncate(m.latest.title)} — @${m.latest.by}`,
        accent: 'text-emerald-100',
      });
    }
  }

  // Admin filter: when `enabledKeys` is an array the broadcaster has
  // explicitly picked which items to show. `null` (default) shows every
  // available item.
  const filteredItems = Array.isArray(enabledKeys)
    ? items.filter((it) => enabledKeys.includes(it.key))
    : items;

  if (filteredItems.length === 0) return null;

  // Quadruple the items so the marquee viewport always falls inside the
  // track (a single copy can be narrower than 1900px on desktop). With
  // four copies + a -25% scroll the seam aligns to a copy boundary and
  // visual content is identical at the wrap → seamless loop.
  const loop = [...filteredItems, ...filteredItems, ...filteredItems, ...filteredItems];

  const atTop = position === 'top';
  // Top-mode: stack ABOVE the navbar (top:0). Navbar offsets itself via
  // --stats-ticker-top-h so existing pt-24 page padding still clears both.
  // Bottom-mode: stack just above the follower ticker.
  const positionStyle = atTop
    ? { top: 0 }
    : { bottom: 'var(--follower-ticker-h, 0px)' };
  const innerCls = atTop
    ? 'border-b border-fuchsia-500/30 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 shadow-[0_8px_24px_-8px_rgba(168,85,247,0.30)]'
    : 'border-t border-fuchsia-500/30 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 shadow-[0_-8px_24px_rgba(168,85,247,0.15)]';

  return (
    <div
      ref={wrapperRef}
      className="stats-ticker fixed inset-x-0 z-[55]"
      style={positionStyle}
      data-position={atTop ? 'top' : 'bottom'}
      data-testid="stats-ticker"
    >
      <div className={innerCls}>
        <div className="max-w-7xl mx-auto flex items-center gap-3 pl-3 pr-3 sm:pl-5 sm:pr-3 py-1">
          {/* LIVE STATS label */}
          <div className="hidden sm:flex shrink-0 items-center gap-2 pr-3 border-r border-white/10">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-fuchsia-400" />
            </span>
            <Radio className="w-3.5 h-3.5 text-fuchsia-200" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-fuchsia-100">
              Stats
            </span>
          </div>

          {/* Marquee viewport */}
          <div
            className="stats-ticker__viewport flex-1 overflow-hidden relative"
            data-testid="stats-ticker-marquee"
          >
            <div
              className="stats-ticker__track flex items-center whitespace-nowrap"
              style={{
                '--stats-ticker-duration': `${durationS}s`,
              }}
            >
              {loop.map((it, i) => {
                const Icon = it.icon;
                return (
                  <span
                    key={`${it.label}-${i}`}
                    className="inline-flex items-center gap-2 mr-6 shrink-0 text-sm"
                    data-testid={i < filteredItems.length ? `stats-ticker-item-${i}` : undefined}
                  >
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/5 border border-fuchsia-400/30">
                      <Icon className={`w-3 h-3 ${it.accent}`} />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-fuchsia-200/70">
                      {it.label}
                    </span>
                    <span className={`font-bold ${it.accent}`}>{it.value}</span>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Close */}
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Verberg stats-balk"
            data-testid="stats-ticker-close"
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full ring-1 ring-red-500/60 text-red-400 hover:text-white hover:bg-red-500/20 hover:ring-red-400 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
